import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/AdminDashboard.css';
import { DOCUMENT_OPTIONS, parseDocumentList, formatDateTimeLocal } from '../utils/helpers';
import { approvalAPI } from '../services/approvalApi';

const QUICK_FILL_DEFAULTS = {
  departments: [
    'Academics', 'Finance', 'Health Services', 'Kitchen & Catering',
    'Administration', 'Operations', 'Security', 'Transport',
    'IT & Communications', 'Library', 'Sports & Extracurricular'
  ],
  reportsTo: [
    'Director of Schools',
    'Head Teacher',
    'Director of Studies',
    'Bursar / Finance Director',
    'School Administrator',
    'Principal',
    'Human Resources Manager'
  ],
  education: [
    'Secondary School Certificate (A2)',
    'A2 in Food Production, Culinary Arts or Hospitality',
    'Diploma in Culinary Arts, Food Production or Hospitality Management',
    "Bachelor's Degree in a related field",
    "Bachelor's in Science Education or a related field",
    "Bachelor's in Accounting or Finance",
    'Registered Nurse with a valid license',
    "Master's Degree"
  ],
  experience: [
    'At least 1-2 years of relevant experience',
    'At least 3-5 years of relevant experience',
    'At least 5 years of relevant experience',
    'Experience in a school or institutional environment preferred',
    'At least 1-2 years in a supervisory or team-lead position',
    'Fresh graduates are encouraged to apply'
  ],
  responsibilities: [
    'Oversee and coordinate all daily operations in this area',
    'Supervise and direct staff; assign duties and monitor performance',
    'Ensure work is completed on time according to school schedules',
    'Maintain high standards of quality, hygiene and safety',
    'Prepare requisitions and manage inventory and stock levels',
    'Keep accurate records and prepare periodic reports',
    'Respond to feedback from students, staff and administration'
  ],
  skills: [
    'Strong leadership and staff-supervision skills',
    'Good communication and teamwork skills',
    'Good organization and time management skills',
    'Ability to work under pressure and meet strict schedules',
    'Attention to detail and commitment to quality',
    'Good problem-solving and decision-making skills'
  ]
};

const QUICK_FILL_FIELDS = [
  { key: 'departments', label: 'Departments', placeholder: 'e.g. Academics, Finance, Health Services...' },
  { key: 'reportsTo', label: 'Reports To', placeholder: 'e.g. Head Teacher, Principal, Bursar...' },
  { key: 'education', label: 'Education Required', placeholder: "e.g. Bachelor's Degree in a related field" },
  { key: 'experience', label: 'Experience Required', placeholder: 'e.g. At least 3-5 years of relevant experience' },
  { key: 'responsibilities', label: 'Main Responsibilities', placeholder: 'e.g. Supervise and direct staff; assign duties and monitor performance' },
  { key: 'skills', label: 'Key Skills', placeholder: 'e.g. Good communication and teamwork skills' }
];

const AdminDashboard = ({ adminUser, onLogout }) => {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    enrolled: 0
  });
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminSection, setNewAdminSection] = useState('students');
  const [newAdminRole, setNewAdminRole] = useState('section_admin');
  const [adminMsg, setAdminMsg] = useState('');

  const [deadline, setDeadline] = useState('');
  const [startDate, setStartDate] = useState('');
  const [deadlineMsg, setDeadlineMsg] = useState('');
  const [nationalExamInput, setNationalExamInput] = useState({});
  const [placementExamInput, setPlacementExamInput] = useState({});
  const [viewApp, setViewApp] = useState(null);
  const [resultModal, setResultModal] = useState(null);
  const [resultForm, setResultForm] = useState({ placementMath: '', placementEnglish: '', placementKinyarwanda: '', placementGeneralKnowledge: '', nesaMarks: '' });
  const [resultComputed, setResultComputed] = useState(null);
  const [resultMsg, setResultMsg] = useState('');
  const [sendingResult, setSendingResult] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  const [tab, setTab] = useState(() => (adminUser?.section === 'careers' ? 'careers' : 'students'));
  const [careerStats, setCareerStats] = useState({ total: 0, pending: 0, reviewed: 0, shortlisted: 0, hired: 0, rejected: 0 });
  const [jobApps, setJobApps] = useState([]);
  const [positions, setPositions] = useState([]);
  const [careerLoading, setCareerLoading] = useState(false);
  const [careerFilter, setCareerFilter] = useState('all');
  const [careerPositionFilter, setCareerPositionFilter] = useState('');
  const [careerSearch, setCareerSearch] = useState('');
  const [positionForm, setPositionForm] = useState({ title: '', department: '', location: '', employmentType: 'Full-time', description: '', requirements: '', deadline: '', requiredDocuments: [] });
  const [positionAdvertFile, setPositionAdvertFile] = useState(null);
  const [editingAdvertPath, setEditingAdvertPath] = useState('');
  const [editingPosition, setEditingPosition] = useState(null);
  const [positionMsg, setPositionMsg] = useState('');
  const [viewJobApp, setViewJobApp] = useState(null);
  const [rejectJobModal, setRejectJobModal] = useState(null);
  const [careerSubTab, setCareerSubTab] = useState('open');

  const [departments, setDepartments] = useState([]);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });
  const [deptMsg, setDeptMsg] = useState('');
  const [editDept, setEditDept] = useState(null);

  const uniqueClasses = [...new Set(applications.map(a => a.classAppliedFor).filter(Boolean))].sort();

  const careerPositionsOpen = positions.filter(p => p.isActive && (!p.deadline || new Date(p.deadline).getTime() > Date.now()));
  const careerPositionsExpired = positions.filter(p => !p.isActive || (p.deadline && new Date(p.deadline).getTime() <= Date.now()));
  const careerAdmins = adminUsers.filter(a => a.isSuperAdmin || a.section === 'careers');

  const renderPositionRow = (pos) => (
    <div key={pos.id} className="position-row">
      <span className="position-title">{pos.title}</span>
      <span className="position-meta">
        {[pos.department, pos.location, pos.employmentType].filter(Boolean).join(' • ')}
        {pos.deadline && ` • Deadline: ${new Date(pos.deadline).toLocaleString()}`}
        {parseDocumentList(pos.requiredDocuments).length > 0 && ` • ${parseDocumentList(pos.requiredDocuments).length} required document(s)`}
      </span>
      <span className={`position-status ${pos.isActive ? 'active' : 'closed'}`}>
        {pos.isActive ? 'Open' : 'Closed'}
      </span>
      {canDecideCareers && (
        <>
          <button onClick={() => handleEditPosition(pos)}>Edit</button>
          <button onClick={() => handleTogglePosition(pos.id)}>{pos.isActive ? 'Close' : 'Publish'}</button>
          <button className="danger" onClick={() => handleDeletePosition(pos.id)}>Delete</button>
          <a href={`/api/admin/careers/positions/${pos.id}/download-files`} target="_blank" rel="noopener noreferrer" className="btn-download">Download Files</a>
        </>
      )}
    </div>
  );

  const userRole = adminUser?.role || (adminUser?.isSuperAdmin ? 'super_admin' : 'section_admin');
  const userSection = adminUser?.section || (adminUser?.isSuperAdmin ? 'both' : 'students');
  const isSuper = userRole === 'super_admin';
  const canManageUsers = isSuper;
  const canAddViewers = isSuper || userRole === 'section_admin';
  const canDecideStudents = isSuper || (userRole === 'section_admin' && (userSection === 'students' || userSection === 'both'));
  const canDecideCareers = isSuper || (userRole === 'section_admin' && (userSection === 'careers' || userSection === 'both'));
  const showStudentsTab = isSuper || userSection === 'students' || userSection === 'both';
  const showCareersTab = isSuper || userSection === 'careers' || userSection === 'both';
  const showDepartmentsTab = isSuper;
  const isViewer = userRole === 'viewer';

  const isImage = (f) => /\.(jpg|jpeg|png|gif|bmp)$/i.test(f);
  const isPdf = (f) => /\.pdf$/i.test(f);

  const handlePrint = (app) => {
    const w = window.open('', '_blank', 'width=800,height=600');
    const docsHtml = app.reportCardPath ? app.reportCardPath.split(',').map((f, i) => {
      const ext = f.split('.').pop().toLowerCase();
      const isImg = ['jpg','jpeg','png','gif','bmp'].includes(ext);
      const isPdfExt = ext === 'pdf';
      return `<li><strong>Report ${i+1}:</strong> ${f}${isImg ? `<br><img src="/uploads/${f}" style="max-width:100%;max-height:400px;margin:4px 0;border:1px solid #ccc">` : ''}${isPdfExt ? `<br><embed src="/uploads/${f}#toolbar=0" type="application/pdf" style="width:100%;height:400px;margin:4px 0;border:1px solid #ccc">` : ''}</li>`;
    }).join('') : '<li>Not uploaded</li>';
    const payHtml = app.paymentProofPath ? (() => {
      const ext = app.paymentProofPath.split('.').pop().toLowerCase();
      const isImg = ['jpg','jpeg','png','gif','bmp'].includes(ext);
      const isPdfExt = ext === 'pdf';
      return `${app.paymentProofPath}${isImg ? `<br><img src="/uploads/${app.paymentProofPath}" style="max-width:100%;max-height:400px;margin:4px 0;border:1px solid #ccc">` : ''}${isPdfExt ? `<br><embed src="/uploads/${app.paymentProofPath}#toolbar=0" type="application/pdf" style="width:100%;height:400px;margin:4px 0;border:1px solid #ccc">` : ''}`;
    })() : 'Not uploaded';
    w.document.write(`
      <html><head><title>Application - ${app.firstName} ${app.lastName}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:40px;font-size:13px;color:#222;max-width:900px;margin:0 auto}
        h1{font-size:20px;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:4px}
        h2{font-size:14px;color:#007bff;text-transform:uppercase;margin:20px 0 8px}
        p{margin:3px 0}
        .s{margin-bottom:14px;padding:8px 12px;background:#f5f5f5;border-radius:4px;page-break-inside:avoid}
        ul{margin:4px 0;padding-left:20px}
        li{margin-bottom:8px}
        .b{font-weight:bold}
        hr{border:none;border-top:1px dashed #ccc;margin:4px 0}
        img,embed{page-break-inside:avoid;page-break-after:always}
        @media print{body{padding:20px}img{max-height:500px !important}embed{height:500px !important}}
      </style></head><body>
      <h1>Application Details</h1>
      <p><span class="b">App #:</span> ${app.applicationNumber || app.id}</p>
      <p><span class="b">Applicant:</span> ${app.firstName} ${app.lastName} &nbsp;|&nbsp; <span class="b">Email:</span> ${app.applicantEmail}</p>
      <hr>
      <div class="s"><h2>Student Information</h2>
      <p><span class="b">Gender:</span> ${app.gender} &nbsp;|&nbsp; <span class="b">Birth:</span> ${app.birthDate ? new Date(app.birthDate).toLocaleDateString() : '-'}</p>
      <p><span class="b">Religion:</span> ${app.religion || '-'} &nbsp;|&nbsp; <span class="b">Nationality:</span> ${app.nationality}</p>
      <p><span class="b">Class Applied:</span> ${app.classAppliedFor}</p>
      <p><span class="b">Address:</span> ${app.village}, ${app.cell}, ${app.sector}, ${app.district}, ${app.province}</p></div>
      <div class="s"><h2>Father Information</h2>
      <p><span class="b">Name:</span> ${app.fatherNames}</p><p><span class="b">Email:</span> ${app.fatherEmail}</p><p><span class="b">Phone:</span> ${app.fatherPhoneNumber}</p></div>
      <div class="s"><h2>Mother Information</h2>
      <p><span class="b">Name:</span> ${app.motherNames}</p><p><span class="b">Email:</span> ${app.motherEmail}</p><p><span class="b">Phone:</span> ${app.motherPhoneNumber}</p></div>
      <div class="s"><h2>Academic Information</h2>
      <p><span class="b">Previous Year:</span> ${app.previousAcademicYear}</p><p><span class="b">Previous Class:</span> ${app.previousClass}</p>
      <p><span class="b">Previous School:</span> ${app.previousSchool || '-'}</p><p><span class="b">Marks:</span> ${app.annualPreviousMarks}%</p></div>
      <div class="s"><h2>Additional Info</h2>
      <p><span class="b">Siblings:</span> ${app.siblingsAtSchool}${app.siblingsAtSchool === 'Yes' ? ' - ' + (app.siblingsList || '') : ''}</p>
      <p><span class="b">Insurance:</span> ${app.insuranceName || '-'}</p><p><span class="b">Parent Occupation:</span> ${app.parentOccupation || '-'}</p></div>
      ${app.guardianNames ? `<div class="s"><h2>Guardian</h2><p><span class="b">Name:</span> ${app.guardianNames}</p><p><span class="b">Email:</span> ${app.guardianEmail || '-'}</p><p><span class="b">Phone:</span> ${app.guardianPhoneNumber || '-'}</p></div>` : ''}
      <div class="s"><h2>Documents</h2>
      <p><span class="b">Report Cards:</span></p><ul>${docsHtml}</ul>
      <p style="margin-top:12px"><span class="b">Payment Proof:</span> ${payHtml}</p></div>
      <div class="s"><h2>Status</h2>
      <p><span class="b">Status:</span> ${app.status || 'Pending'}</p><p><span class="b">Reviewed By:</span> ${app.reviewedBy || '-'}</p>
      <p><span class="b">Submitted:</span> ${new Date(app.createdAt).toLocaleString()}</p></div>
      <p style="text-align:center;margin-top:30px;color:#999;font-size:11px">Hope Haven Christian School — Printed ${new Date().toLocaleString()}</p>
      </body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 1000);
  };

  useEffect(() => {
    fetchDashboard();
    if (tab === 'careers') fetchCareerDashboard();
    if (tab === 'departments' && showDepartmentsTab) loadDepartments();
    if (canManageUsers || canAddViewers) fetchAdminUsers();
    if (isSuper) fetchDeadline();
    if (canDecideCareers) fetchQuickFill();
  }, [adminUser, tab]);

  const fetchDeadline = async () => {
    try {
      const res = await axios.get('/api/settings/deadline');
      setStartDate(res.data.startDate || '');
      setDeadline(res.data.deadline || '');
    } catch (e) { /* ignore */ }
  };

  const handleSetDeadline = async (e) => {
    e.preventDefault();
    setDeadlineMsg('');
    try {
      await axios.post('/api/admin/settings/deadline', { startDate, deadline });
      setDeadlineMsg('Settings saved successfully');
    } catch (err) {
      setDeadlineMsg(err.response?.data?.message || 'Failed to save settings');
    }
  };

  const handleNationalExam = async (id, results) => {
    try {
      await axios.post(`/api/admin/applications/${id}/national-exam`, { results });
      fetchDashboard();
    } catch (error) {
      console.error('Failed to update national exam results', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get('/api/admin/dashboard');
      setStats(response.data.stats);
      setApplications(response.data.applications);
    } catch (error) {
      console.error('Failed to fetch dashboard', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get('/api/admin/admin-users');
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch admin users', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`/api/admin/applications/${id}/approve`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to approve application', error);
    }
  };

  const handleReview = async (id) => {
    try {
      await axios.post(`/api/admin/applications/${id}/review`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to review application', error);
    }
  };

  const handlePlacementExam = async (id, marks) => {
    try {
      await axios.post(`/api/admin/applications/${id}/placement-exam`, { marks });
      fetchDashboard();
    } catch (error) {
      console.error('Failed to save placement exam marks', error);
    }
  };

  const handleReject = async (id, reason) => {
    try {
      await axios.post(`/api/admin/applications/${id}/reject`, { reason });
      setRejectModal(null);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to reject application', error);
    }
  };

  const handleEnroll = async (id) => {
    try {
      await axios.post(`/api/admin/applications/${id}/enroll`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to enroll student', error);
    }
  };

  const handleFinalReject = async (id) => {
    try {
      await axios.post(`/api/admin/applications/${id}/final-reject`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to final reject', error);
    }
  };

  const handleWaitlist = async (id) => {
    try {
      await axios.post(`/api/admin/applications/${id}/waitlist`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to waitlist application', error);
    }
  };

  const handleOverallWaitlist = async (id) => {
    try {
      await axios.post(`/api/admin/applications/${id}/overall-waitlist`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to overall waitlist application', error);
    }
  };

  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [bulkActionMsg, setBulkActionMsg] = useState('');
  const [examMsg, setExamMsg] = useState('');
  const examFileRef = React.createRef();

  const toggleAppSelect = (id) => {
    setSelectedAppIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pendingIds = filteredApplications.filter(a => a.status === 'Pending' && canDecideStudents).map(a => a.id);
    if (selectedAppIds.length === pendingIds.length && pendingIds.length > 0) {
      setSelectedAppIds([]);
    } else {
      setSelectedAppIds(pendingIds);
    }
  };

  const handleBulkApprove = async () => {
    if (!selectedAppIds.length) { setBulkActionMsg('Select at least one application'); return; }
    setBulkActionMsg('');
    try {
      await axios.post('/api/admin/applications/bulk/approve', { ids: selectedAppIds });
      setSelectedAppIds([]);
      setBulkActionMsg('Applications approved successfully');
      fetchDashboard();
    } catch (error) {
      setBulkActionMsg(error.response?.data?.message || 'Failed to approve applications');
    }
  };

  const handleBulkReject = async () => {
    if (!selectedAppIds.length) { setBulkActionMsg('Select at least one application'); return; }
    setBulkActionMsg('');
    try {
      await axios.post('/api/admin/applications/bulk/reject', { ids: selectedAppIds, reason: bulkRejectReason || 'general' });
      setSelectedAppIds([]);
      setBulkActionMsg('Applications rejected successfully');
      setBulkRejectReason('');
      fetchDashboard();
    } catch (error) {
      setBulkActionMsg(error.response?.data?.message || 'Failed to reject applications');
    }
  };

  const handleBulkWaitlist = async () => {
    if (!selectedAppIds.length) { setBulkActionMsg('Select at least one application'); return; }
    setBulkActionMsg('');
    try {
      await axios.post('/api/admin/applications/bulk/waitlist', { ids: selectedAppIds });
      setSelectedAppIds([]);
      setBulkActionMsg('Applications waitlisted successfully');
      fetchDashboard();
    } catch (error) {
      setBulkActionMsg(error.response?.data?.message || 'Failed to waitlist applications');
    }
  };

  const handleExportExcel = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.filter && filters.filter !== 'all') params.set('filter', filters.filter);
      if (filters.classFilter) params.set('classFilter', filters.classFilter);
      if (filters.search) params.set('search', filters.search);
      const response = await axios.get(`/api/admin/export/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `applications-${filters.filter || 'all'}-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
    } catch (error) {
      console.error('Failed to export Excel', error);
    }
  };

  const handleDownloadExamTemplate = async () => {
    try {
      setExamMsg('');
      const response = await axios.get('/api/admin/applications/results-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exam-results-template-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      setExamMsg('Exam results template downloaded. Fill in the marks and upload the file below.');
    } catch (error) {
      setExamMsg(error.response?.data?.message || 'Failed to download template');
    }
  };

  const handleExamFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setExamMsg('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post('/api/admin/applications/results-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const notFound = response.data.notFoundList?.length ? ` (${response.data.notFoundList.length} application number(s) not found)` : '';
      setExamMsg(`Uploaded successfully: ${response.data.updated} application(s) updated${notFound}.`);
      fetchDashboard();
    } catch (error) {
      setExamMsg(error.response?.data?.message || 'Failed to upload exam results');
    } finally {
      e.target.value = '';
    }
  };

  const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  const computeResult = (form) => {
    const subs = [num(form.placementMath), num(form.placementEnglish), num(form.placementKinyarwanda), num(form.placementGeneralKnowledge)];
    const filled = subs.filter(n => n !== null);
    const nesa = num(form.nesaMarks);
    let total = null, avg = null, grand = null, overall = null;
    if (filled.length) {
      total = Math.round(filled.reduce((a, b) => a + b, 0) * 10) / 10;
      avg = Math.round((total / 4) * 10) / 10;
      if (nesa !== null) {
        grand = Math.round((avg + nesa) * 10) / 10;
        overall = Math.round((grand / 2) * 10) / 10;
      }
    }
    return { total, avg, grand, overall };
  };

  const openResultModal = (app) => {
    setResultForm({
      placementMath: app.placementMath || '',
      placementEnglish: app.placementEnglish || '',
      placementKinyarwanda: app.placementKinyarwanda || '',
      placementGeneralKnowledge: app.placementGeneralKnowledge || '',
      nesaMarks: app.nesaMarks || ''
    });
    setResultComputed(computeResult({
      placementMath: app.placementMath || '',
      placementEnglish: app.placementEnglish || '',
      placementKinyarwanda: app.placementKinyarwanda || '',
      placementGeneralKnowledge: app.placementGeneralKnowledge || '',
      nesaMarks: app.nesaMarks || ''
    }));
    setResultMsg('');
    setResultModal(app);
  };

  const handleResultFormChange = (field, value) => {
    const next = { ...resultForm, [field]: value };
    setResultForm(next);
    setResultComputed(computeResult(next));
    setResultMsg('');
  };

  const handleSaveResult = async () => {
    if (!resultModal) return;
    try {
      const payload = {};
      ['placementMath', 'placementEnglish', 'placementKinyarwanda', 'placementGeneralKnowledge', 'nesaMarks'].forEach(f => {
        const v = resultForm[f] !== '' ? resultForm[f] : null;
        payload[f] = v;
      });
      const response = await axios.post(`/api/admin/applications/${resultModal.id}/result`, payload);
      setResultMsg(response.data.message || 'Result saved successfully');
      setResultComputed({ total: response.data.placementTotal, avg: response.data.placementAverage, grand: response.data.grandTotal, overall: response.data.overallAverage });
      fetchDashboard();
    } catch (error) {
      setResultMsg(error.response?.data?.message || 'Failed to save result');
    }
  };

  const handleSendResult = async () => {
    if (!resultModal || sendingResult) return;
    setSendingResult(true);
    setResultMsg('');
    try {
      const response = await axios.post(`/api/admin/applications/${resultModal.id}/send-result`);
      setResultMsg(response.data.message || 'Result email sent successfully');
      setResultModal({ ...resultModal, resultEmailed: 1 });
      fetchDashboard();
    } catch (error) {
      setResultMsg(error.response?.data?.message || 'Failed to send result email');
    } finally {
      setSendingResult(false);
    }
  };

  const fetchCareerDashboard = async () => {
    try {
      setCareerLoading(true);
      const response = await axios.get('/api/admin/careers/dashboard');
      setCareerStats(response.data.stats);
      setJobApps(response.data.applications);
      setPositions(response.data.positions);
    } catch (error) {
      console.error('Failed to fetch career dashboard', error);
    } finally {
      setCareerLoading(false);
    }
  };

  const handlePositionFormChange = (e) => {
    const { name, value } = e.target;
    setPositionForm(prev => ({ ...prev, [name]: value }));
    setPositionMsg('');
  };

  const toggleRequiredDoc = (doc) => {
    setPositionForm(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.includes(doc)
        ? prev.requiredDocuments.filter(d => d !== doc)
        : [...prev.requiredDocuments, doc]
    }));
  };

  const resetPositionForm = () => {
    setPositionForm({ title: '', department: '', location: '', employmentType: 'Full-time', description: '', requirements: '', deadline: '', requiredDocuments: [] });
    setPositionAdvertFile(null);
    setEditingAdvertPath('');
  };

  const [quickPick, setQuickPick] = useState({ department: '', reportsTo: '', education: '', experience: '', responsibility: '', skills: '' });

  const [quickFillOptions, setQuickFillOptions] = useState(QUICK_FILL_DEFAULTS);
  const [quickFillDrafts, setQuickFillDrafts] = useState({});
  const [quickFillMsg, setQuickFillMsg] = useState('');
  const [quickFillError, setQuickFillError] = useState('');

  const fetchQuickFill = async () => {
    try {
      const res = await axios.get('/api/admin/settings/quickfill');
      const merged = { ...QUICK_FILL_DEFAULTS };
      Object.keys(merged).forEach(k => {
        if (Array.isArray(res.data?.[k]) && res.data[k].length) merged[k] = res.data[k];
      });
      setQuickFillOptions(merged);
      setQuickFillDrafts(Object.fromEntries(Object.keys(merged).map(k => [k, merged[k].join('\n')])));
    } catch (e) { /* keep defaults */ }
  };

  const saveQuickFillList = async (key) => {
    setQuickFillMsg('');
    setQuickFillError('');
    try {
      const values = (quickFillDrafts[key] || '').split('\n').map(s => s.trim()).filter(Boolean);
      await axios.post('/api/admin/settings/quickfill', { field: key, values });
      const updated = { ...quickFillOptions, [key]: values };
      setQuickFillOptions(updated);
      setQuickFillDrafts({ ...quickFillDrafts, [key]: values.join('\n') });
      setQuickFillMsg('Saved successfully');
      setTimeout(() => setQuickFillMsg(''), 2500);
    } catch (err) {
      setQuickFillError(err.response?.data?.message || 'Failed to save');
    }
  };

  const clearPositionField = (field) => {
    setPositionForm(prev => ({ ...prev, [field]: '' }));
    setPositionMsg('');
  };

  const applyQuickPick = (key, value) => {
    if (!value) return;
    if (key === 'department') {
      setPositionForm(prev => ({ ...prev, department: value }));
    } else if (key === 'reportsTo') {
      setPositionForm(prev => {
        const line = `Position Reports To: ${value}`;
        const cur = prev.description || '';
        if (cur.startsWith('Position Reports To:')) {
          const rest = cur.split('\n').slice(1).join('\n').replace(/^\n+/, '');
          return { ...prev, description: rest ? `${line}\n\n${rest}` : line };
        }
        return { ...prev, description: cur ? `${line}\n\n${cur}` : line };
      });
    } else if (key === 'responsibility') {
      setPositionForm(prev => {
        const line = `- ${value}`;
        const cur = prev.description || '';
        if (cur.split('\n').includes(line)) return prev;
        return { ...prev, description: cur ? `${cur}\n${line}` : line };
      });
    } else if (key === 'education' || key === 'experience' || key === 'skills') {
      setPositionForm(prev => {
        const line = key === 'education' ? `Education: ${value}` : value;
        const lines = (prev.requirements || '').split('\n').filter(Boolean);
        if (lines.includes(line)) return prev;
        lines.push(line);
        return { ...prev, requirements: lines.join('\n') };
      });
    }
    setQuickPick(prev => ({ ...prev, [key]: '' }));
    setPositionMsg('');
  };

  const handleAddPosition = async (e) => {
    e.preventDefault();
    setPositionMsg('');
    try {
      const fd = new FormData();
      Object.keys(positionForm).forEach(k => {
        fd.append(k, k === 'requiredDocuments' ? JSON.stringify(positionForm[k]) : (positionForm[k] || ''));
      });
      if (positionAdvertFile) fd.append('jobAdvert', positionAdvertFile);
      if (editingPosition) {
        await axios.put(`/api/admin/careers/positions/${editingPosition.id}`, fd);
        setPositionMsg('Position updated successfully');
      } else {
        await axios.post('/api/admin/careers/positions', fd);
        setPositionMsg('Position created successfully');
      }
      resetPositionForm();
      setEditingPosition(null);
      fetchCareerDashboard();
      setCareerSubTab('open');
    } catch (error) {
      setPositionMsg(error.response?.data?.message || 'Failed to save position');
    }
  };

  const handleRemoveAdvert = async () => {
    if (!editingPosition) return;
    try {
      await axios.delete(`/api/admin/careers/positions/${editingPosition.id}/advert`);
      setEditingAdvertPath('');
      setPositionMsg('Job advert removed');
    } catch (error) {
      setPositionMsg(error.response?.data?.message || 'Failed to remove job advert');
    }
  };

  const handleEditPosition = (pos) => {
    setEditingPosition(pos);
    setCareerSubTab('add');
    setPositionForm({
      title: pos.title || '',
      department: pos.department || '',
      location: pos.location || '',
      employmentType: pos.employmentType || 'Full-time',
      description: pos.description || '',
      requirements: pos.requirements || '',
      deadline: formatDateTimeLocal(pos.deadline),
      requiredDocuments: parseDocumentList(pos.requiredDocuments)
    });
    setPositionAdvertFile(null);
    setEditingAdvertPath(pos.jobAdvertPath || '');
    setPositionMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTogglePosition = async (id) => {
    try {
      await axios.post(`/api/admin/careers/positions/${id}/toggle`);
      fetchCareerDashboard();
    } catch (error) {
      console.error('Failed to toggle position', error);
    }
  };

  const handleDeletePosition = async (id) => {
    if (!window.confirm('Delete this position? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/admin/careers/positions/${id}`);
      fetchCareerDashboard();
    } catch (error) {
      console.error('Failed to delete position', error);
    }
  };

  const handleReviewJob = async (id) => {
    try {
      await axios.post(`/api/admin/careers/applications/${id}/review`);
      fetchCareerDashboard();
    } catch (error) {
      console.error('Failed to review job application', error);
    }
  };

  const handleShortlistJob = async (id) => {
    try {
      await axios.post(`/api/admin/careers/applications/${id}/shortlist`);
      fetchCareerDashboard();
    } catch (error) {
      console.error('Failed to shortlist job application', error);
    }
  };

  const handleHireJob = async (id) => {
    if (!window.confirm('Confirm hiring this applicant? An email will be sent.')) return;
    try {
      await axios.post(`/api/admin/careers/applications/${id}/hire`);
      fetchCareerDashboard();
    } catch (error) {
      console.error('Failed to hire applicant', error);
    }
  };

  const handleRejectJob = async (id, reason) => {
    try {
      await axios.post(`/api/admin/careers/applications/${id}/reject`, { reason });
      setRejectJobModal(null);
      fetchCareerDashboard();
    } catch (error) {
      console.error('Failed to reject job application', error);
    }
  };

  const handleCareerExport = async () => {
    try {
      const params = new URLSearchParams();
      if (careerFilter && careerFilter !== 'all') params.set('filter', careerFilter);
      if (careerPositionFilter) params.set('positionFilter', careerPositionFilter);
      if (careerSearch) params.set('search', careerSearch);
      const response = await axios.get(`/api/admin/careers/applications/export/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `career-applications-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
    } catch (error) {
      console.error('Failed to export career Excel', error);
    }
  };

  const loadDepartments = async () => {
    try { const res = await approvalAPI.getDepartments(); setDepartments(res.data); } catch (e) {}
  };
  const handleAddDepartment = async (e) => {
    e.preventDefault();
    setDeptMsg('');
    try { await approvalAPI.createDepartment(deptForm); setDeptMsg('Department created'); setDeptForm({ name: '', description: '' }); loadDepartments(); } catch (err) { setDeptMsg(err.response?.data?.message || 'Failed to create department'); }
  };
  const handleEditDepartment = async () => {
    if (!editDept) return;
    setDeptMsg('');
    try { await approvalAPI.updateDepartment(editDept.id, editDept); setDeptMsg('Department updated'); setEditDept(null); loadDepartments(); } catch (err) { setDeptMsg(err.response?.data?.message || 'Failed to update department'); }
  };
  const handleToggleDepartment = async (id, current) => {
    try { await approvalAPI.updateDepartment(id, { isActive: current ? 0 : 1 }); loadDepartments(); } catch (err) { console.error(err); }
  };
  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    try { await approvalAPI.deleteDepartment(id); loadDepartments(); } catch (err) { setDeptMsg(err.response?.data?.message || 'Cannot delete department'); }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setAdminMsg('');
    try {
      await axios.post('/api/admin/admin-users', { email: newAdminEmail, name: newAdminName, section: newAdminSection, role: newAdminRole });
      setAdminMsg('Admin added successfully');
      setNewAdminEmail('');
      setNewAdminName('');
      fetchAdminUsers();
    } catch (error) {
      setAdminMsg(error.response?.data?.message || 'Failed to add admin');
    }
  };

  const handleAddViewer = async (e) => {
    e.preventDefault();
    setAdminMsg('');
    try {
      await axios.post('/api/admin/admin-users/viewer', { email: newAdminEmail, name: newAdminName });
      setAdminMsg('Viewer added successfully');
      setNewAdminEmail('');
      setNewAdminName('');
      fetchAdminUsers();
    } catch (error) {
      setAdminMsg(error.response?.data?.message || 'Failed to add viewer');
    }
  };

  const handleToggleAdmin = async (id, currentActive) => {
    try {
      if (currentActive) {
        await axios.post(`/api/admin/admin-users/${id}/deactivate`);
      } else {
        await axios.post(`/api/admin/admin-users/${id}/activate`);
      }
      fetchAdminUsers();
    } catch (error) {
      console.error('Failed to toggle admin', error);
    }
  };

  const handleDeleteAdmin = async (id, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/admin-users/${id}`);
      setAdminMsg('Admin deleted permanently');
      fetchAdminUsers();
    } catch (error) {
      setAdminMsg(error.response?.data?.message || 'Failed to delete admin');
    }
  };

  const filteredApplications = (filter === 'all' ? applications : applications.filter(app => app.status === filter))
    .filter(app => !classFilter || app.classAppliedFor === classFilter)
    .filter(app => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (app.applicationNumber?.toLowerCase().includes(q)) ||
             String(app.id).includes(q) ||
             (app.firstName?.toLowerCase().includes(q)) ||
             (app.lastName?.toLowerCase().includes(q)) ||
             `${app.firstName} ${app.lastName}`.toLowerCase().includes(q) ||
             (app.applicantEmail?.toLowerCase().includes(q)) ||
             (app.fatherEmail?.toLowerCase().includes(q)) ||
             (app.fatherNames?.toLowerCase().includes(q)) ||
             (app.motherNames?.toLowerCase().includes(q)) ||
             (app.fatherPhoneNumber?.includes(q)) ||
             (app.motherPhoneNumber?.includes(q));
    });

  const filteredJobApps = (careerFilter === 'all' ? jobApps : jobApps.filter(app => app.status === careerFilter))
    .filter(app => !careerPositionFilter || String(app.position_id) === String(careerPositionFilter))
    .filter(app => {
      if (!careerSearch) return true;
      const q = careerSearch.toLowerCase();
      return (app.applicationNumber?.toLowerCase().includes(q)) ||
             String(app.id).includes(q) ||
             (app.firstName?.toLowerCase().includes(q)) ||
             (app.lastName?.toLowerCase().includes(q)) ||
             `${app.firstName} ${app.lastName}`.toLowerCase().includes(q) ||
             (app.email?.toLowerCase().includes(q)) ||
             (app.positionTitle?.toLowerCase().includes(q)) ||
             (app.phone?.includes(q));
    });

  if (loading) return <div className="loading">Loading dashboard...</div>;

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <img src="/school.png" alt="Hope Haven Christian School" className="admin-logo" />
        <div>
          <h1>Hope Haven Christian School</h1>
          <p className="admin-subtitle">Admin Dashboard</p>
        </div>
        <div className="admin-user-info">
          <span className="admin-email">{adminUser?.email}</span>
          <span className="admin-badges">
            {adminUser?.isSuperAdmin && <span className="super-badge">Super Admin</span>}
            {!adminUser?.isSuperAdmin && (
              <>
                {adminUser?.role && (
                  <span className="super-badge" style={{ background: adminUser.role === 'viewer' ? '#0e7490' : '#2563eb' }}>
                    {adminUser.role === 'viewer' ? 'Viewer' : 'Section Admin'}
                  </span>
                )}
                {adminUser?.section && (
                  <span className="super-badge" style={{ background: '#475569' }}>
                    {adminUser.section === 'careers' ? 'Careers' : 'Students'}
                  </span>
                )}
              </>
            )}
          </span>
          <button onClick={onLogout} className="signout-btn">Sign Out</button>
        </div>
      </div>

      <main className="admin-content">
      {/* Tab Switcher */}
      <div className="dashboard-tabs">
        {showStudentsTab && (
          <button className={`dashboard-tab ${tab === 'students' ? 'active' : ''}`} onClick={() => setTab('students')}>
            Student Applications
          </button>
        )}
        {showCareersTab && (
          <button className={`dashboard-tab ${tab === 'careers' ? 'active' : ''}`} onClick={() => setTab('careers')}>
            Careers
          </button>
        )}
        {showDepartmentsTab && (
          <button className={`dashboard-tab ${tab === 'departments' ? 'active' : ''}`} onClick={() => setTab('departments')}>
            Departments
          </button>
        )}
        {canDecideCareers && (
          <button className={`dashboard-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
            Settings
          </button>
        )}
      </div>

      {isViewer && (
        <div className="career-note">
          <strong>Viewer mode:</strong> you can view applications and mark documents as reviewed to collaborate,
          but only the {userSection === 'careers' ? 'HR administrator' : 'section administrator'} can make decisions
          (approve, reject, shortlist, hire, etc.).
        </div>
      )}

      {tab === 'students' && (<>
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Applications</h3>
          <p className="stat-number">{stats.total}</p>
        </div>
        <div className="stat-card pending">
          <h3>Pending</h3>
          <p className="stat-number">{stats.pending}</p>
        </div>
        <div className="stat-card accepted">
          <h3>Accepted</h3>
          <p className="stat-number">{stats.accepted}</p>
        </div>
        <div className="stat-card rejected">
          <h3>Rejected</h3>
          <p className="stat-number">{stats.rejected}</p>
        </div>
        <div className="stat-card" style={{borderLeftColor: '#17a2b8'}}>
          <h3>Enrolled</h3>
          <p className="stat-number">{stats.enrolled}</p>
        </div>
      </div>

      {/* Super Admin: Application Period Management */}
      {isSuper && (
        <div className="admin-management">
          <h2>Application Period</h2>
          <form onSubmit={handleSetDeadline} className="add-admin-form">
            <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            <span style={{lineHeight:'38px',fontWeight:600,color:'#666'}}>→</span>
            <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} required />
            <button type="submit">Save Period</button>
          </form>
          {deadlineMsg && <p className="admin-msg">{deadlineMsg}</p>}
        </div>
      )}

      {/* Manage Admins: Super Admin adds section admins, Section Admins add viewers */}
      {canManageUsers || canAddViewers ? (
        <div className="admin-management">
          <h2>{canManageUsers ? 'Admin Management' : 'Manage Reviewers (Viewers)'}</h2>
          {canManageUsers && (
            <form onSubmit={handleAddAdmin} className="add-admin-form">
              <input
                type="email"
                placeholder="admin@hopehavenschool.org"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Full Name"
                value={newAdminName}
                onChange={(e) => setNewAdminName(e.target.value)}
              />
              <select value={newAdminSection} onChange={(e) => setNewAdminSection(e.target.value)}>
                <option value="students">Students Section</option>
                <option value="careers">Careers (HR) Section</option>
              </select>
              <select value={newAdminRole} onChange={(e) => setNewAdminRole(e.target.value)}>
                <option value="section_admin">Section Admin (HR/Admissions)</option>
                <option value="viewer">Viewer (Review Only)</option>
              </select>
              <button type="submit">Add Admin</button>
            </form>
          )}
          {canAddViewers && !canManageUsers && (
            <form onSubmit={handleAddViewer} className="add-admin-form">
              <input
                type="email"
                placeholder="viewer@hopehavenschool.org"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Full Name"
                value={newAdminName}
                onChange={(e) => setNewAdminName(e.target.value)}
              />
              <button type="submit">Add Viewer</button>
            </form>
          )}
          <p className="career-note" style={{background:'#e8f5e9', borderLeftColor:'#28a745', color:'#155724'}}>
            {canManageUsers
              ? 'Section Admins manage their section and make decisions. Viewers can review documents but cannot make decisions. Careers section admins (HR) can add viewers for their section.'
              : `Viewers you add will only be able to view applications and mark documents as reviewed. Only you (${userSection === 'careers' ? 'HR' : 'Admissions'} section admin) can make final decisions.`}
          </p>
          {adminMsg && <p className="admin-msg">{adminMsg}</p>}
          <div className="admin-users-list">
            {adminUsers.map(admin => {
              const isRowSuper = admin.isSuperAdmin || admin.role === 'super_admin';
              return (
                <div key={admin.id} className={`admin-user-row ${!admin.isActive ? 'inactive' : ''}`}>
                  <span>{admin.email}</span>
                  <span>{admin.name || '-'}</span>
                  <span>
                    {isRowSuper ? (
                      <span className="super-badge">Super Admin</span>
                    ) : (
                      <>
                        <span className="super-badge" style={{background: admin.role === 'viewer' ? '#17a2b8' : '#007bff', marginRight: '4px'}}>
                          {admin.role === 'viewer' ? 'Viewer' : 'Section Admin'}
                        </span>
                        <span className="super-badge" style={{background: '#6c757d'}}>
                          {admin.section === 'careers' ? 'Careers' : 'Students'}
                        </span>
                      </>
                    )}
                  </span>
                  {!isRowSuper && (
                    <>
                      <button onClick={() => handleToggleAdmin(admin.id, admin.isActive)}>
                        {admin.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      {!admin.isActive && (
                        <button className="danger" onClick={() => handleDeleteAdmin(admin.id, admin.email)}>
                          Delete Permanently
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Export, Search and Filter Section */}
      <div className="controls">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="Pending">Pending</option>
            <option value="Accepted">Accepted</option>
            <option value="Waitlisted">Waitlisted</option>
            <option value="Rejected">Rejected</option>
            <option value="Enrolled">Enrolled</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Class:</label>
          <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="">All Classes</option>
            {uniqueClasses.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Search:</label>
          <input type="text" placeholder="Name, Email, Phone, App #..." value={search} onChange={(e) => setSearch(e.target.value)} className="search-input" />
        </div>
        <button onClick={() => handleExportExcel({ filter, classFilter, search })} className="export-btn">
          Download Excel ({filteredApplications.length})
        </button>
      </div>

      {/* Applications Table */}
      <div className="applications-table">
        <h2>Applications ({filteredApplications.length})</h2>
        {canDecideStudents && (
          <div className="bulk-actions">
            <span className="bulk-count">{selectedAppIds.length} selected</span>
            <button onClick={handleBulkApprove} className="btn-approve" disabled={!selectedAppIds.length}>Accept Selected</button>
            <button onClick={handleBulkWaitlist} className="btn-waitlist" disabled={!selectedAppIds.length}>Waitlist Selected</button>
            <button onClick={handleBulkReject} className="btn-reject" disabled={!selectedAppIds.length}>Reject Selected</button>
            <select value={bulkRejectReason} onChange={(e) => setBulkRejectReason(e.target.value)} className="bulk-reason">
              <option value="">Reject reason...</option>
              <option value="low grade">Low Grade (Below 80%)</option>
              <option value="fees not paid">Application Fees Not Paid</option>
              <option value="general">General / Other</option>
            </select>
            {bulkActionMsg && <span className="bulk-msg">{bulkActionMsg}</span>}
          </div>
        )}
        {canDecideStudents && (
          <div className="exam-results-controls" style={{display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap', marginBottom:'16px'}}>
            <button onClick={handleDownloadExamTemplate} className="export-btn">Download Exam Results Template</button>
            <label className="export-btn" style={{cursor:'pointer'}}>
              Upload Exam Results
              <input type="file" accept=".xlsx" ref={examFileRef} onChange={handleExamFileChange} style={{display:'none'}} />
            </label>
            {examMsg && <span style={{fontSize:'13px', color:'#475569'}}>{examMsg}</span>}
          </div>
        )}
        {filteredApplications.length === 0 ? (
          <p>No applications found</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                  <tr>
                    {canDecideStudents && <th><input type="checkbox" onChange={toggleSelectAll} checked={filteredApplications.filter(a => a.status === 'Pending').length > 0 && selectedAppIds.length === filteredApplications.filter(a => a.status === 'Pending').length} /></th>}
                    <th>App #</th>
                    <th>Name</th>
                    <th>Applicant Email</th>
                    <th>Class Applied</th>
                    <th>Status</th>
                    <th>Reviewed By</th>
                    <th>Files</th>
                    <th>Marks</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
              </thead>
              <tbody>
                {filteredApplications.map(app => (
                  <tr key={app.id} className={`status-${app.status?.toLowerCase()}`}>
                    {canDecideStudents && (
                      <td>
                        <input type="checkbox" checked={selectedAppIds.includes(app.id)} onChange={() => toggleAppSelect(app.id)} disabled={app.status !== 'Pending'} />
                      </td>
                    )}
                    <td className="text-xs font-mono">{app.applicationNumber || app.id}</td>
                    <td>{app.firstName} {app.lastName}</td>
                    <td>{app.applicantEmail || app.fatherEmail}</td>
                    <td>{app.classAppliedFor}</td>
                    <td><span className={`status-badge ${app.status?.toLowerCase()}`}>{app.status || 'Pending'}</span></td>
                    <td>{app.reviewedBy || '-'}</td>
                    <td className="file-links">
                      {app.reportCardPath && app.reportCardPath.split(',').map((f, i) => (
                        <a key={i} href={`/uploads/${f}`} target="_blank" rel="noopener noreferrer" className="file-link">R{i+1}</a>
                      ))}
                      {app.paymentProofPath && <a href={`/uploads/${app.paymentProofPath}`} target="_blank" rel="noopener noreferrer" className="file-link">Pay</a>}
                      {!app.reportCardPath && !app.paymentProofPath && <span className="text-gray-400">-</span>}
                    </td>
                    <td>
                      <div className="flex flex-col gap-1">
                        {['Senior 1', 'Senior 4 MS I', 'Senior 4 MS II', 'Senior 4 ARH', 'TVET LEVEL 3 SOD', 'Associate Nursing Program'].includes(app.classAppliedFor) ? (
                          <>
                            {app.placementExamMarks && <span className="text-xs text-blue-600 font-medium" title="Placement Exam">P: {app.placementExamMarks}</span>}
                            {app.nationalExamResults ? (
                              <span className="text-green-600 font-semibold cursor-pointer text-xs" title={app.nationalExamResults}>N: {app.nationalExamResults}</span>
                            ) : canDecideStudents ? (
                              <div className="exam-input flex gap-1">
                                <input type="text" placeholder="N.Exam" className="w-14 px-1 py-0.5 border rounded text-xs"
                                  value={nationalExamInput[app.id] || ''}
                                  onChange={(e) => setNationalExamInput({...nationalExamInput, [app.id]: e.target.value})} />
                                <button onClick={() => handleNationalExam(app.id, nationalExamInput[app.id])}
                                  className="btn-approve text-xs px-2 py-0.5" disabled={!nationalExamInput[app.id]}>Save</button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </div>
                    </td>
                    <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                    <td className="actions">
                      <button onClick={() => setViewApp(app)} className="btn-view">View</button>
                      {canDecideStudents && (
                        <button onClick={() => openResultModal(app)} className="btn-waitlist" title="Enter / view exam result">Result</button>
                      )}
                      {app.status === 'Pending' && !app.reviewedBy && (
                        <button onClick={() => handleReview(app.id)} className="btn-view" style={{background:'#ffc107',color:'#333'}}>Review Docs</button>
                      )}
                      {canDecideStudents && app.status === 'Pending' && (
                        <>
                          <button onClick={() => handleApprove(app.id)} className="btn-approve">Accept</button>
                          <button onClick={() => handleWaitlist(app.id)} className="btn-waitlist">Waitlist</button>
                          <button onClick={() => setRejectModal(app.id)} className="btn-reject">Reject</button>
                        </>
                      )}
                      {canDecideStudents && app.status === 'Accepted' && (
                        <>
                          <div className="exam-input flex gap-1">
                            <input type="text" placeholder="Placement" className="w-14 px-1 py-0.5 border rounded text-xs"
                              value={placementExamInput[app.id] || app.placementExamMarks || ''}
                              onChange={(e) => setPlacementExamInput({...placementExamInput, [app.id]: e.target.value})} />
                            <button onClick={() => handlePlacementExam(app.id, placementExamInput[app.id])}
                              className="btn-approve text-xs px-2 py-0.5" disabled={!placementExamInput[app.id]}>Save</button>
                          </div>
                          <button onClick={() => handleEnroll(app.id)} className="btn-approve">Overall Accept</button>
                          <button onClick={() => handleOverallWaitlist(app.id)} className="btn-waitlist">Overall Waitlist</button>
                          <button onClick={() => handleFinalReject(app.id)} className="btn-reject">Overall Reject</button>
                        </>
                      )}
                      {canDecideStudents && app.status === 'Waitlisted' && (
                        <>
                          <button onClick={() => handleEnroll(app.id)} className="btn-approve">Overall Accept</button>
                          <button onClick={() => handleApprove(app.id)} className="btn-approve">Accept</button>
                          <button onClick={() => setRejectModal(app.id)} className="btn-reject">Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Reject Reason Modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal-content" style={{maxWidth: '400px'}} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setRejectModal(null)}>&times;</button>
            <h2>Select Rejection Reason</h2>
            <div className="modal-body" style={{gap: '10px'}}>
              <button onClick={() => handleReject(rejectModal, 'low grade')} className="btn-reject" style={{width:'100%', padding:'12px', fontSize:'14px'}}>Low Grade (Below 80%)</button>
              <button onClick={() => handleReject(rejectModal, 'fees not paid')} className="btn-reject" style={{width:'100%', padding:'12px', fontSize:'14px'}}>Application Fees Not Paid</button>
            </div>
          </div>
        </div>
      )}

      {/* Exam Result Modal */}
      {resultModal && (
        <div className="modal-overlay" onClick={() => setResultModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setResultModal(null)}>&times;</button>
            <h2>Exam Result - {resultModal.firstName} {resultModal.lastName}</h2>
            <p className="text-sm text-gray-500 mb-3"><strong>Application #:</strong> {resultModal.applicationNumber || resultModal.id} &nbsp;|&nbsp; <strong>Status:</strong> {resultModal.status || 'Pending'}</p>
            <div className="result-form" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px'}}>
              {[
                { key: 'placementMath', label: 'Placement - Mathematics' },
                { key: 'placementEnglish', label: 'Placement - English' },
                { key: 'placementKinyarwanda', label: 'Placement - Kinyarwanda' },
                { key: 'placementGeneralKnowledge', label: 'Placement - General Knowledge' },
                { key: 'nesaMarks', label: 'NESA Result' }
              ].map(f => (
                <div key={f.key}>
                  <label style={{display:'block', fontSize:'12px', marginBottom:'4px', color:'#475569'}}>{f.label}</label>
                  <input type="number" min="0" max="100" className="w-full px-2 py-1.5 border rounded text-sm"
                    value={resultForm[f.key]}
                    onChange={(e) => handleResultFormChange(f.key, e.target.value)} />
                </div>
              ))}
            </div>
            {resultComputed && (resultComputed.total !== null || resultComputed.grand !== null) && (
              <div style={{background:'#f8fafc', borderRadius:'8px', padding:'14px', marginBottom:'16px', border:'1px solid #e5e7eb'}}>
                <p style={{margin:'2px 0'}}><strong>Placement Total:</strong> {resultComputed.total !== null ? `${resultComputed.total}` : '-'} / 400</p>
                <p style={{margin:'2px 0'}}><strong>Placement Average:</strong> {resultComputed.avg !== null ? `${resultComputed.avg}` : '-'} / 100</p>
                <p style={{margin:'2px 0'}}><strong>Grand Total (Avg + NESA):</strong> {resultComputed.grand !== null ? `${resultComputed.grand}` : '-'} / 200</p>
                <p style={{margin:'2px 0'}}><strong>Overall Average:</strong> {resultComputed.overall !== null ? `${resultComputed.overall}` : '-'} / 100</p>
              </div>
            )}
            {resultMsg && <p style={{fontSize:'13px', marginBottom:'10px', color:'#1a56db'}}>{resultMsg}</p>}
            <div style={{display:'flex', gap:'10px', justifyContent:'flex-end', flexWrap:'wrap'}}>
              <button onClick={handleSaveResult} className="btn-approve">Save Result</button>
              <button onClick={handleSendResult} className="btn-waitlist" disabled={sendingResult || resultModal.resultEmailed}>
                {resultModal.resultEmailed ? 'Result Email Sent' : sendingResult ? 'Sending...' : 'Send Result to Applicant'}
              </button>
              <button onClick={() => setResultModal(null)} className="btn-reject">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewApp && (
        <div className="modal-overlay" onClick={() => setViewApp(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setViewApp(null)}>&times;</button>
            <button onClick={() => handlePrint(viewApp)} className="btn-view" style={{position:'absolute', top:'12px', right:'100px', fontSize:'13px'}}>Print</button>
            <button onClick={() => window.open('/api/admin/applications/' + viewApp.id + '/download')} className="btn-view" style={{position:'absolute', top:'12px', right:'12px', fontSize:'13px', background:'#28a745'}}>Download</button>
            <h2>Application Details - {viewApp.firstName} {viewApp.lastName}</h2>
            <p className="text-sm text-gray-500 mb-4"><strong>Application #:</strong> {viewApp.applicationNumber || viewApp.id}</p>
            <div className="modal-body" id="print-area">
              <div className="detail-section">
                <h3>Student Information</h3>
                <p><strong>Name:</strong> {viewApp.firstName} {viewApp.lastName}</p>
                <p><strong>Gender:</strong> {viewApp.gender}</p>
                <p><strong>Birth Date:</strong> {viewApp.birthDate && new Date(viewApp.birthDate).toLocaleDateString()}</p>
                <p><strong>Religion:</strong> {viewApp.religion || '-'}</p>
                <p><strong>Nationality:</strong> {viewApp.nationality}</p>
                <p><strong>Class Applied:</strong> {viewApp.classAppliedFor}</p>
                <p><strong>Province:</strong> {viewApp.province}</p>
                <p><strong>District:</strong> {viewApp.district}</p>
                <p><strong>Sector:</strong> {viewApp.sector}</p>
                <p><strong>Cell:</strong> {viewApp.cell}</p>
                <p><strong>Village:</strong> {viewApp.village}</p>
              </div>
              <div className="detail-section">
                <h3>Father Information</h3>
                <p><strong>Name:</strong> {viewApp.fatherNames}</p>
                <p><strong>Email:</strong> {viewApp.fatherEmail}</p>
                <p><strong>Phone:</strong> {viewApp.fatherPhoneNumber}</p>
                <p><strong>ID:</strong> {viewApp.fatherID}</p>
              </div>
              <div className="detail-section">
                <h3>Mother Information</h3>
                <p><strong>Name:</strong> {viewApp.motherNames}</p>
                <p><strong>Email:</strong> {viewApp.motherEmail}</p>
                <p><strong>Phone:</strong> {viewApp.motherPhoneNumber}</p>
                <p><strong>ID:</strong> {viewApp.motherID}</p>
              </div>
              <div className="detail-section">
                <h3>Academic Information</h3>
                <p><strong>Previous Year:</strong> {viewApp.previousAcademicYear}</p>
                <p><strong>Previous Class:</strong> {viewApp.previousClass}</p>
                <p><strong>Previous School:</strong> {viewApp.previousSchool || '-'}</p>
                <p><strong>Previous Marks:</strong> {viewApp.annualPreviousMarks}%</p>
              </div>
              <div className="detail-section">
                <h3>Additional Information</h3>
                <p><strong>Siblings at School:</strong> {viewApp.siblingsAtSchool}</p>
                {viewApp.siblingsAtSchool === 'Yes' && <p><strong>Siblings List:</strong> {viewApp.siblingsList}</p>}
                <p><strong>Comment:</strong> {viewApp.comment || '-'}</p>
                <p><strong>Insurance:</strong> {viewApp.insuranceName || '-'}</p>
                <p><strong>Parent Occupation:</strong> {viewApp.parentOccupation || '-'}</p>
                <p><strong>Willing to Pay 100%:</strong> {viewApp.willingToPayFull === 'Yes' ? 'Yes' : 'No'}</p>
              </div>
              {viewApp.guardianNames && (
                <div className="detail-section">
                  <h3>Guardian Information</h3>
                  <p><strong>Name:</strong> {viewApp.guardianNames}</p>
                  <p><strong>Email:</strong> {viewApp.guardianEmail || '-'}</p>
                  <p><strong>Phone:</strong> {viewApp.guardianPhoneNumber || '-'}</p>
                  <p><strong>Comment:</strong> {viewApp.guardianComment || '-'}</p>
                </div>
              )}
              <div className="detail-section">
                <h3>Documents</h3>
                <p><strong>Report Cards:</strong></p>
                {viewApp.reportCardPath ? viewApp.reportCardPath.split(',').map((f, i) => (
                  <p key={i}><a href={`/uploads/${f}`} target="_blank" rel="noopener noreferrer" className="file-link">Report {i+1}</a></p>
                )) : <p className="text-gray-400">Not uploaded</p>}
                <p className="mt-2"><strong>Payment Proof:</strong> {viewApp.paymentProofPath ? <a href={`/uploads/${viewApp.paymentProofPath}`} target="_blank" rel="noopener noreferrer" className="file-link">View</a> : <span className="text-gray-400">Not uploaded</span>}</p>
              </div>
              <div className="detail-section">
                <h3>Status</h3>
                <p><strong>Status:</strong> <span className={`status-badge ${viewApp.status?.toLowerCase()}`}>{viewApp.status || 'Pending'}</span></p>
                <p><strong>Reviewed By:</strong> {viewApp.reviewedBy || '-'}</p>
                <p><strong>Submitted:</strong> {new Date(viewApp.createdAt).toLocaleString()}</p>
                {viewApp.nationalExamResults && <p><strong>National Exam Results:</strong> {viewApp.nationalExamResults}</p>}
                <p><strong>Student ID:</strong> {viewApp.sdmsCode ? `SDMS: ${viewApp.sdmsCode}` : viewApp.nationalExamIndex ? `Exam Index: ${viewApp.nationalExamIndex}` : viewApp.internationalCode ? `International: ${viewApp.internationalCode}` : '-'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      </>)}

      {tab === 'careers' && (
        <>
        {/* Career Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Job Applications</h3>
            <p className="stat-number">{careerStats.total}</p>
          </div>
          <div className="stat-card pending">
            <h3>Pending</h3>
            <p className="stat-number">{careerStats.pending}</p>
          </div>
          <div className="stat-card" style={{borderLeftColor: '#6c757d'}}>
            <h3>Reviewed</h3>
            <p className="stat-number">{careerStats.reviewed}</p>
          </div>
          <div className="stat-card" style={{borderLeftColor: '#17a2b8'}}>
            <h3>Shortlisted</h3>
            <p className="stat-number">{careerStats.shortlisted}</p>
          </div>
          <div className="stat-card accepted">
            <h3>Hired</h3>
            <p className="stat-number">{careerStats.hired}</p>
          </div>
          <div className="stat-card rejected">
            <h3>Rejected</h3>
            <p className="stat-number">{careerStats.rejected}</p>
          </div>
        </div>

        {/* Careers Sub-Tabs */}
        <div className="career-subtabs">
          <button className={`career-subtab ${careerSubTab === 'open' ? 'active' : ''}`} onClick={() => setCareerSubTab('open')}>
            Open Positions ({careerPositionsOpen.length})
          </button>
          <button className={`career-subtab ${careerSubTab === 'expired' ? 'active' : ''}`} onClick={() => setCareerSubTab('expired')}>
            Expired Positions ({careerPositionsExpired.length})
          </button>
          {canDecideCareers && (
            <button className={`career-subtab ${careerSubTab === 'add' ? 'active' : ''}`} onClick={() => setCareerSubTab('add')}>
              {editingPosition ? 'Edit Position' : 'Add Position'}
            </button>
          )}
          <button className={`career-subtab ${careerSubTab === 'applications' ? 'active' : ''}`} onClick={() => setCareerSubTab('applications')}>
            Applications ({filteredJobApps.length})
          </button>
          {(canManageUsers || canAddViewers) && (
            <button className={`career-subtab ${careerSubTab === 'admins' ? 'active' : ''}`} onClick={() => setCareerSubTab('admins')}>
              Manage Admin
            </button>
          )}
        </div>

        {/* Open Positions */}
        {careerSubTab === 'open' && (
          <div className="admin-management">
            <h2>Open Positions</h2>
            <p className="career-note" style={{background:'#e8f5e9', borderLeftColor:'#28a745', color:'#155724'}}>
              Positions with an active application deadline. Use <strong>Add Position</strong> to create new openings.
            </p>
            <div className="admin-users-list">
              {careerPositionsOpen.map(renderPositionRow)}
              {careerPositionsOpen.length === 0 && <p className="no-data">No open positions with an active deadline.</p>}
            </div>
          </div>
        )}

        {/* Expired Positions */}
        {careerSubTab === 'expired' && (
          <div className="admin-management">
            <h2>Expired Positions</h2>
            <p className="career-note" style={{background:'#fff3cd', borderLeftColor:'#ffc107', color:'#856404'}}>
              Positions whose deadline has passed or that were closed. Edit a position to reopen it with a new deadline, or publish it again.
            </p>
            <div className="admin-users-list">
              {careerPositionsExpired.map(renderPositionRow)}
              {careerPositionsExpired.length === 0 && <p className="no-data">No expired or closed positions.</p>}
            </div>
          </div>
        )}

        {/* Add Position */}
        {careerSubTab === 'add' && canDecideCareers && (
          <div className="admin-management">
            <h2>{editingPosition ? `Edit Position: ${editingPosition.title}` : 'Add Position'}</h2>
            <form onSubmit={handleAddPosition} className="career-position-form">
              <div className="quick-fill-box">
                <div className="quick-fill-title">Quick Fill — pick common options to auto-fill the details below</div>
                <div className="form-row">
                  <select value={quickPick.department} onChange={(e) => applyQuickPick('department', e.target.value)}>
                    <option value="">Department...</option>
                    {quickFillOptions.departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={quickPick.reportsTo} onChange={(e) => applyQuickPick('reportsTo', e.target.value)}>
                    <option value="">Reports To...</option>
                    {quickFillOptions.reportsTo.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={quickPick.education} onChange={(e) => applyQuickPick('education', e.target.value)}>
                    <option value="">Education Required...</option>
                    {quickFillOptions.education.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={quickPick.experience} onChange={(e) => applyQuickPick('experience', e.target.value)}>
                    <option value="">Experience Required...</option>
                    {quickFillOptions.experience.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={quickPick.responsibility} onChange={(e) => applyQuickPick('responsibility', e.target.value)}>
                    <option value="">Add Main Responsibility...</option>
                    {quickFillOptions.responsibilities.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={quickPick.skills} onChange={(e) => applyQuickPick('skills', e.target.value)}>
                    <option value="">Add Key Skill...</option>
                    {quickFillOptions.skills.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <input type="text" name="title" placeholder="Position Title *" value={positionForm.title} onChange={handlePositionFormChange} required />
                <input type="text" name="department" placeholder="Department" value={positionForm.department} onChange={handlePositionFormChange} />
                <input type="text" name="location" placeholder="Location" value={positionForm.location} onChange={handlePositionFormChange} />
                <select name="employmentType" value={positionForm.employmentType} onChange={handlePositionFormChange}>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                </select>
              </div>
              <div className="form-row">
                <label className="career-field-label">Application Deadline</label>
                <input type="datetime-local" name="deadline" value={positionForm.deadline} onChange={handlePositionFormChange} />
                <label className="career-field-label">Required Documents (auto-added to application form)</label>
              </div>
              <div className="form-row advert-row">
                <label className="career-field-label">Job Advert (signed PDF — optional, applicants can download it)</label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setPositionAdvertFile(e.target.files[0] || null)}
                />
                {positionAdvertFile && <p className="no-data" style={{color:'#28a745'}}>New file selected: {positionAdvertFile.name}</p>}
                {!positionAdvertFile && editingAdvertPath && (
                  <div className="advert-current">
                    <p className="no-data">Current advert:</p>
                    <a href={`/uploads/${editingAdvertPath}`} target="_blank" rel="noopener noreferrer" className="file-link">View / Download</a>
                    <button type="button" onClick={handleRemoveAdvert} className="btn-reject">Remove</button>
                  </div>
                )}
              </div>
              <div className="form-row docs-checkboxes">
                {DOCUMENT_OPTIONS.map(doc => (
                  <label key={doc} className="doc-checkbox">
                    <input
                      type="checkbox"
                      checked={positionForm.requiredDocuments.includes(doc)}
                      onChange={() => toggleRequiredDoc(doc)}
                    />
                    <span>{doc}</span>
                  </label>
                ))}
                {positionForm.requiredDocuments.length === 0 && <p className="no-data">No documents selected — applicants will only need to upload their CV.</p>}
              </div>
              <div className="textarea-block">
                <div className="textarea-head">
                  <label className="career-field-label">Job Description</label>
                  <button type="button" className="btn-clear" onClick={() => clearPositionField('description')}>Clear</button>
                </div>
                <textarea name="description" placeholder="Job Description — Quick Fill options add the Reports To line and responsibilities here automatically" value={positionForm.description} onChange={handlePositionFormChange}></textarea>
              </div>
              <div className="textarea-block">
                <div className="textarea-head">
                  <label className="career-field-label">Requirements (one per line)</label>
                  <button type="button" className="btn-clear" onClick={() => clearPositionField('requirements')}>Clear</button>
                </div>
                <textarea name="requirements" placeholder="Requirements — Quick Fill options add Education, Experience and Skills lines here automatically" value={positionForm.requirements} onChange={handlePositionFormChange}></textarea>
              </div>
              <div className="form-row">
                <button type="submit">{editingPosition ? 'Update Position' : 'Add Position'}</button>
                {editingPosition && (
                  <button type="button" onClick={() => { setEditingPosition(null); resetPositionForm(); }} className="btn-reject">
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
            {positionMsg && <p className={`admin-msg ${positionMsg.includes('Failed') || positionMsg.includes('required') ? 'error' : ''}`} style={positionMsg.includes('Failed') || positionMsg.includes('required') ? {color: '#dc3545'} : undefined}>{positionMsg}</p>}
          </div>
        )}

        {/* Applications */}
        {careerSubTab === 'applications' && (
          <>
          <div className="controls">
            <div className="filter-group">
              <label>Status:</label>
              <select value={careerFilter} onChange={(e) => setCareerFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="Pending">Pending</option>
                <option value="Reviewed">Reviewed</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Hired">Hired</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Position:</label>
              <select value={careerPositionFilter} onChange={(e) => setCareerPositionFilter(e.target.value)}>
                <option value="">All Positions</option>
                {positions.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Search:</label>
              <input type="text" placeholder="Name, Email, Position, App #..." value={careerSearch} onChange={(e) => setCareerSearch(e.target.value)} className="search-input" />
            </div>
            <button onClick={handleCareerExport} className="export-btn">
              Download Excel ({filteredJobApps.length})
            </button>
          </div>

          <div className="applications-table">
            <h2>Job Applications ({filteredJobApps.length})</h2>
            {careerLoading ? (
              <p className="loading">Loading career applications...</p>
            ) : filteredJobApps.length === 0 ? (
              <p className="no-data">No job applications found</p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>App #</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Position</th>
                      <th>Experience</th>
                      <th>CV</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobApps.map(app => (
                      <tr key={app.id} className={`status-${app.status?.toLowerCase()}`}>
                        <td className="text-xs font-mono">{app.applicationNumber || app.id}</td>
                        <td>{app.firstName} {app.lastName}</td>
                        <td>{app.email}</td>
                        <td>{app.positionTitle}</td>
                        <td>{app.yearsOfExperience ? `${app.yearsOfExperience} yr` : '-'}</td>
                        <td className="file-links">
                          {app.cvPath ? <a href={`/uploads/${app.cvPath}`} target="_blank" rel="noopener noreferrer" className="file-link">CV</a> : <span className="text-gray-400">-</span>}
                        </td>
                        <td><span className={`status-badge ${app.status?.toLowerCase()}`}>{app.status || 'Pending'}</span></td>
                        <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                        <td className="actions">
                          <button onClick={() => setViewJobApp(app)} className="btn-view">View</button>
                          {(app.status === 'Pending' || app.status === 'Reviewed') && (
                            <button onClick={() => handleReviewJob(app.id)} className="btn-view" style={{background:'#ffc107',color:'#333'}}>Review</button>
                          )}
                          {canDecideCareers && app.status !== 'Shortlisted' && app.status !== 'Hired' && app.status !== 'Rejected' && (
                            <button onClick={() => handleShortlistJob(app.id)} className="btn-shortlist">Shortlist</button>
                          )}
                          {canDecideCareers && (app.status === 'Shortlisted') && (
                            <button onClick={() => handleHireJob(app.id)} className="btn-approve">Hire</button>
                          )}
                          {canDecideCareers && app.status !== 'Hired' && app.status !== 'Rejected' && (
                            <button onClick={() => setRejectJobModal(app.id)} className="btn-reject">Reject</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
        )}

        {/* Manage Admin */}
        {careerSubTab === 'admins' && (canManageUsers || canAddViewers) && (
          <div className="admin-management">
            <h2>{canManageUsers ? 'Recruitment Admin Management' : 'Manage Reviewers (Viewers)'}</h2>
            {canManageUsers && (
              <form onSubmit={handleAddAdmin} className="add-admin-form">
                <input
                  type="email"
                  placeholder="admin@hopehavenschool.org"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={newAdminName}
                  onChange={(e) => setNewAdminName(e.target.value)}
                />
                <select value={newAdminSection} onChange={(e) => setNewAdminSection(e.target.value)}>
                  <option value="students">Students Section</option>
                  <option value="careers">Careers (HR) Section</option>
                </select>
                <select value={newAdminRole} onChange={(e) => setNewAdminRole(e.target.value)}>
                  <option value="section_admin">Section Admin (HR/Admissions)</option>
                  <option value="viewer">Viewer (Review Only)</option>
                </select>
                <button type="submit">Add Admin</button>
              </form>
            )}
            {canAddViewers && !canManageUsers && (
              <form onSubmit={handleAddViewer} className="add-admin-form">
                <input
                  type="email"
                  placeholder="viewer@hopehavenschool.org"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={newAdminName}
                  onChange={(e) => setNewAdminName(e.target.value)}
                />
                <button type="submit">Add Viewer</button>
              </form>
            )}
            <p className="career-note" style={{background:'#e8f5e9', borderLeftColor:'#28a745', color:'#155724'}}>
              {canManageUsers
                ? 'Admins shown here are those with access to the Careers (recruitment) section. Viewers can review documents but cannot make decisions. Deactivate to suspend access, then delete permanently once deactivated.'
                : 'Viewers you add will only be able to view applications and mark documents as reviewed. Only you (HR section admin) can make final decisions.'}
            </p>
            {adminMsg && <p className="admin-msg">{adminMsg}</p>}
            <div className="admin-users-list">
              {careerAdmins.map(admin => {
                const isRowSuper = admin.isSuperAdmin || admin.role === 'super_admin';
                return (
                  <div key={admin.id} className={`admin-user-row ${!admin.isActive ? 'inactive' : ''}`}>
                    <span>{admin.email}</span>
                    <span>{admin.name || '-'}</span>
                    <span>
                      {isRowSuper ? (
                        <span className="super-badge">Super Admin</span>
                      ) : (
                        <>
                          <span className="super-badge" style={{background: admin.role === 'viewer' ? '#17a2b8' : '#007bff', marginRight: '4px'}}>
                            {admin.role === 'viewer' ? 'Viewer' : 'Section Admin'}
                          </span>
                          <span className="super-badge" style={{background: '#6c757d'}}>
                            Careers
                          </span>
                        </>
                      )}
                    </span>
                    {!isRowSuper && (
                      <>
                        <button onClick={() => handleToggleAdmin(admin.id, admin.isActive)}>
                          {admin.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        {!admin.isActive && (
                          <button className="danger" onClick={() => handleDeleteAdmin(admin.id, admin.email)}>
                            Delete Permanently
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {careerAdmins.length === 0 && <p className="no-data">No recruitment admins yet. Add a viewer above to get started.</p>}
            </div>
          </div>
        )}

        {/* Career Reject Modal */}
        {rejectJobModal && (
          <div className="modal-overlay" onClick={() => setRejectJobModal(null)}>
            <div className="modal-content" style={{maxWidth: '400px'}} onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setRejectJobModal(null)}>&times;</button>
              <h2>Reject Application</h2>
              <div className="modal-body" style={{gap: '10px'}}>
                <button onClick={() => handleRejectJob(rejectJobModal, 'not selected')} className="btn-reject" style={{width:'100%', padding:'12px', fontSize:'14px'}}>Not Selected</button>
                <button onClick={() => handleRejectJob(rejectJobModal, 'position filled')} className="btn-reject" style={{width:'100%', padding:'12px', fontSize:'14px'}}>Position Filled</button>
                <button onClick={() => handleRejectJob(rejectJobModal, 'insufficient qualifications')} className="btn-reject" style={{width:'100%', padding:'12px', fontSize:'14px'}}>Insufficient Qualifications</button>
              </div>
            </div>
          </div>
        )}

        {/* Career View Details Modal */}
        {viewJobApp && (
          <div className="modal-overlay" onClick={() => setViewJobApp(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setViewJobApp(null)}>&times;</button>
              <h2>Application Details - {viewJobApp.firstName} {viewJobApp.lastName}</h2>
              <p className="text-sm text-gray-500 mb-4"><strong>Application #:</strong> {viewJobApp.applicationNumber || viewJobApp.id}</p>
              <div className="modal-body">
                <div className="detail-section">
                  <h3>Applicant Information</h3>
                  <p><strong>Full Name:</strong> {viewJobApp.firstName} {viewJobApp.lastName}</p>
                  <p><strong>Email:</strong> {viewJobApp.email}</p>
                  <p><strong>Phone:</strong> {viewJobApp.phone}</p>
                  <p><strong>Position:</strong> {viewJobApp.positionTitle}</p>
                  <p><strong>Education Level:</strong> {viewJobApp.educationLevel || '-'}</p>
                  <p><strong>Years of Experience:</strong> {viewJobApp.yearsOfExperience ? `${viewJobApp.yearsOfExperience} years` : '-'}</p>
                </div>
                {(viewJobApp.coverLetterPath || viewJobApp.coverLetter) && (
                  <div className="detail-section">
                    <h3>Cover Letter</h3>
                    {viewJobApp.coverLetterPath ? (
                      <a href={`/uploads/${viewJobApp.coverLetterPath}`} target="_blank" rel="noopener noreferrer" className="file-link">Download Cover Letter</a>
                    ) : (
                      <p style={{whiteSpace: 'pre-wrap'}}>{viewJobApp.coverLetter}</p>
                    )}
                  </div>
                )}
                <div className="detail-section">
                  <h3>CV / Resume</h3>
                  {viewJobApp.cvPath ? <a href={`/uploads/${viewJobApp.cvPath}`} target="_blank" rel="noopener noreferrer" className="file-link">Download CV</a> : <p className="text-gray-400">Not uploaded</p>}
                </div>
                {(() => {
                  let docs = {};
                  try { docs = JSON.parse(viewJobApp.additionalDocuments || '{}'); } catch (e) { docs = {}; }
                  const entries = Object.entries(docs);
                  return entries.length > 0 ? (
                    <div className="detail-section">
                      <h3>Additional Documents</h3>
                      {entries.map(([label, file]) => (
                        <p key={label}><strong>{label}:</strong> <a href={`/uploads/${file}`} target="_blank" rel="noopener noreferrer" className="file-link">View</a></p>
                      ))}
                    </div>
                  ) : null;
                })()}
                <div className="detail-section">
                  <h3>Status</h3>
                  <p><strong>Status:</strong> <span className={`status-badge ${viewJobApp.status?.toLowerCase()}`}>{viewJobApp.status || 'Pending'}</span></p>
                  <p><strong>Reviewed By:</strong> {viewJobApp.reviewedBy || '-'}</p>
                  {viewJobApp.rejectionReason && <p><strong>Rejection Reason:</strong> {viewJobApp.rejectionReason}</p>}
                  <p><strong>Submitted:</strong> {new Date(viewJobApp.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
      )}

      {tab === 'departments' && showDepartmentsTab && (
        <div className="admin-management">
          <h2>Departments</h2>
          <p className="settings-intro">
            Manage all school departments. These are used in the Approval &amp; Sign-off system for requests and user assignments.
          </p>

          <form onSubmit={handleAddDepartment} className="add-admin-form">
            <input type="text" placeholder="Department name" value={deptForm.name} onChange={(e) => setDeptForm({...deptForm, name: e.target.value})} required />
            <input type="text" placeholder="Description (optional)" value={deptForm.description} onChange={(e) => setDeptForm({...deptForm, description: e.target.value})} />
            <button type="submit">Add Department</button>
          </form>
          {deptMsg && <p className="admin-msg">{deptMsg}</p>}

          {editDept && (
            <div className="add-admin-form" style={{marginTop:'8px', background:'#f0f9ff', padding:'10px', borderRadius:'6px'}}>
              <input type="text" value={editDept.name} onChange={(e) => setEditDept({...editDept, name: e.target.value})} placeholder="Name" required />
              <input type="text" value={editDept.description || ''} onChange={(e) => setEditDept({...editDept, description: e.target.value})} placeholder="Description" />
              <button onClick={handleEditDepartment}>Save</button>
              <button className="btn-reject" onClick={() => setEditDept(null)} style={{marginLeft:'4px'}}>Cancel</button>
            </div>
          )}

          <table className="admin-table" style={{marginTop:'12px'}}>
            <thead>
              <tr><th>Department</th><th>Description</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {departments.map(d => (
                <tr key={d.id}>
                  <td><strong>{d.name}</strong></td>
                  <td>{d.description || '-'}</td>
                  <td>{d.isActive ? <span style={{color:'#28a745'}}>Active</span> : <span style={{color:'#dc3545'}}>Disabled</span>}</td>
                  <td className="actions">
                    <button className="btn-view" onClick={() => setEditDept({...d})}>Edit</button>
                    <button className={d.isActive ? 'btn-reject' : 'btn-approve'} onClick={() => handleToggleDepartment(d.id, d.isActive)}>{d.isActive ? 'Disable' : 'Enable'}</button>
                    <button className="btn-reject" onClick={() => handleDeleteDepartment(d.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {departments.length === 0 && <tr><td colSpan={4} style={{textAlign:'center',color:'#999'}}>No departments found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'settings' && canDecideCareers && (
        <>
        {/* Quick Fill Settings */}
        <div className="admin-management">
          <h2>Quick Fill Settings</h2>
          <p className="settings-intro">
            Edit the common options shown in the <strong>Quick Fill</strong> panel when creating a career position.
            Add one option per line. Changes are saved to the database and apply to all administrators.
          </p>

          {quickFillError && <p className="admin-msg" style={{color: '#dc3545'}}>{quickFillError}</p>}
          {quickFillMsg && <p className="admin-msg" style={{color: '#28a745'}}>{quickFillMsg}</p>}

          <div className="settings-grid">
            {QUICK_FILL_FIELDS.map(f => (
              <div key={f.key} className="settings-card">
                <h3>{f.label}</h3>
                <textarea
                  className="settings-textarea"
                  placeholder={f.placeholder}
                  value={quickFillDrafts[f.key] || ''}
                  onChange={(e) => setQuickFillDrafts(prev => ({ ...prev, [f.key]: e.target.value }))}
                  rows={6}
                />
                <div className="settings-actions">
                  <span className="settings-count">
                    {(quickFillDrafts[f.key] || '').split('\n').map(s => s.trim()).filter(Boolean).length} option(s)
                  </span>
                  <button type="button" onClick={() => saveQuickFillList(f.key)}>Save {f.label}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        </>
      )}
      </main>
    </div>
  );
};

export default AdminDashboard;
